import React from 'react';
import { format } from 'date-fns';
import {
  SwapHoriz as SwapHorizIcon,
  PersonAdd as EntityIcon,
  Settings as SettingsIcon,
  ShoppingCart as PurchaseOrderIcon,
  Science as LabIntakeIcon,
  AttachFile as DocumentIcon,
  AccountBalance as SageIcon,
  Send as SendIcon,
  RateReview as ReviewIcon,
  History as HistoryIcon,
  ChatBubbleOutline as CommentIcon,
} from '@mui/icons-material';
import { WorkflowHistoryItem } from '../../types/workflow/activity';

type ChipColor = 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success' | 'default';

export const getCategoryLabel = (category: string): string => {
  switch (category) {
    case 'StatusTransition':
    case 'Status':
      return 'Status Transition';
    case 'EntityAssignment':
      return 'Entity Assignment';
    case 'Settings':
      return 'Settings';
    case 'PurchaseOrder':
      return 'Purchase Order';
    case 'LabIntake':
      return 'Lab Intake';
    case 'Document':
      return 'Document';
    case 'SageDocument':
      return 'Sage Document';
    case 'ReportDelivery':
      return 'Report Delivery';
    case 'ReviewApproval':
      return 'Review Approval';
    case 'Comment':
      return 'Comment';
    default:
      return category;
  }
};

export const getItemIcon = (item: WorkflowHistoryItem): React.ReactElement => {
  if (item.itemType === 'Transition') {
    return React.createElement(SwapHorizIcon, { fontSize: 'small' });
  }
  if (item.itemType === 'Comment') {
    return React.createElement(CommentIcon, { fontSize: 'small' });
  }
  switch (item.category) {
    case 'EntityAssignment':
      return React.createElement(EntityIcon, { fontSize: 'small' });
    case 'Settings':
      return React.createElement(SettingsIcon, { fontSize: 'small' });
    case 'PurchaseOrder':
      return React.createElement(PurchaseOrderIcon, { fontSize: 'small' });
    case 'LabIntake':
      return React.createElement(LabIntakeIcon, { fontSize: 'small' });
    case 'Document':
      return React.createElement(DocumentIcon, { fontSize: 'small' });
    case 'SageDocument':
      return React.createElement(SageIcon, { fontSize: 'small' });
    case 'ReportDelivery':
      return React.createElement(SendIcon, { fontSize: 'small' });
    case 'ReviewApproval':
      return React.createElement(ReviewIcon, { fontSize: 'small' });
    default:
      return React.createElement(HistoryIcon, { fontSize: 'small' });
  }
};

export const getDotColor = (item: WorkflowHistoryItem): 'primary' | 'error' | 'warning' | 'info' | 'success' => {
  if (item.itemType === 'Comment') return 'success';
  if (item.itemType === 'Transition') return 'primary';
  switch (item.priority) {
    case 'High':
      return 'error';
    case 'Medium':
      return 'warning';
    case 'Low':
      return 'info';
    default:
      return 'primary';
  }
};

export const getCategoryChipColor = (item: WorkflowHistoryItem): ChipColor => {
  if (item.itemType === 'Comment') return 'success';
  if (item.itemType === 'Transition') return 'primary';
  switch (item.category) {
    case 'EntityAssignment':
      return 'primary';
    case 'Settings':
      return 'secondary';
    case 'PurchaseOrder':
      return 'success';
    case 'LabIntake':
      return 'info';
    case 'Document':
      return 'warning';
    case 'SageDocument':
      return 'info';
    case 'ReportDelivery':
      return 'success';
    case 'ReviewApproval':
      return 'warning';
    default:
      return 'default';
  }
};

export const formatDate = (timestamp: string): string => {
  return format(new Date(timestamp), 'MMM d, yyyy');
};

export const formatTime = (timestamp: string): string => {
  return format(new Date(timestamp), 'h:mm a');
};

/** Keys to hide from metadata display -- either redundant with the description or internal IDs */
const HIDDEN_METADATA_KEYS = new Set([
  'toStatus', 'fromStatus',                          // redundant with status chips
  'documentNumber',                                   // already in description text
  'sagePurchaseOrderId', 'sageInvoiceId',            // internal Sage IDs
  'added', 'removed',                                 // already expressed in description counts
]);

export const getVisibleMetadata = (metadata?: Record<string, unknown>): Array<[string, string]> => {
  if (!metadata) return [];
  return Object.entries(metadata)
    .filter(([key]) => !HIDDEN_METADATA_KEYS.has(key))
    .map(([key, value]) => [key, String(value)]);
};
